/**
 * Переезд на новый адрес (этап 27 плана, «PWA и локальная копия»).
 *
 * Установленное приложение — это оболочка из кэша Service Worker на старом
 * домене. Редирект Caddy до неё не доходит: Service Worker отвечает на переход
 * сам, не спрашивая сервер. Поэтому о переезде оболочка узнаёт запросом к API
 * (`GET /v1/moved`), а сервер отвечает по переключателю `MOVED_SCREEN` в
 * `.env` — включить и откатить экран можно без выкладки.
 */

/**
 * Адреса, с которых продукт переехал.
 *
 * Нужны настройкам: ключ входа такого домена подписывается «для старого
 * адреса». Список, а не «всё, кроме текущего»: до переключения на старом
 * адресе тоже бывают ключи нового, и звать новый адрес старым было бы враньём.
 */
export const LEGACY_HOSTS: readonly string[] = ['nedelnik.app'];

/**
 * Последний ответ сервера — в `localStorage`, чтобы экран переезда стоял с
 * первого кадра и без сети. Без памяти приложение сначала показывало бы
 * задачи, а через долю секунды — экран переезда; а запущенное без сети
 * пускало бы копить правки в копии, из которой их уже зовут увозить.
 */
const STORAGE_KEY = 'nedelnik.moved';

/**
 * Адрес переезда, если он годится: абсолютный `http(s)` и не тот, где мы
 * открыты. Свой адрес отсеивается намеренно: экран, зовущий туда, где человек
 * уже стоит, — тупик без выхода.
 */
export function movedOrigin(raw: unknown, here: string): string | undefined {
  if (typeof raw !== 'string') return undefined;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return undefined;
  return url.origin === here ? undefined : url.origin;
}

/** Куда переехали — по памяти устройства; `undefined` — никуда. */
export function readMovedTarget(here = currentOrigin()): string | undefined {
  try {
    return movedOrigin(globalThis.localStorage?.getItem(STORAGE_KEY), here);
  } catch {
    // Приватное окно: памяти нет, экран появится после ответа сервера.
    return undefined;
  }
}

export function rememberMovedTarget(target: string | undefined): void {
  try {
    if (target === undefined) globalThis.localStorage?.removeItem(STORAGE_KEY);
    else globalThis.localStorage?.setItem(STORAGE_KEY, target);
  } catch {
    // Не запомнилось — экран появится после ответа сервера, а не с первого кадра.
  }
}

/**
 * Спросить сервер, куда переехали.
 *
 * Бросает, если сервер не ответил: «не дозвонились» и «не переезжаем» — разные
 * ответы, и первый не должен стирать память о переезде.
 */
export async function fetchMovedTarget(
  options: { baseUrl?: string; fetchImpl?: typeof fetch; here?: string } = {},
): Promise<string | undefined> {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const response = await doFetch(`${options.baseUrl ?? ''}/v1/moved`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`/v1/moved вернул ${String(response.status)}`);

  const body = (await response.json()) as { to?: unknown };
  return movedOrigin(body.to, options.here ?? currentOrigin());
}

/**
 * Тот же путь и та же строка запроса на новом адресе — для переходов, которые
 * не должны теряться по дороге (согласие коннектора).
 */
export function forwardUrl(target: string, at: { pathname: string; search: string }): string {
  return new URL(`${at.pathname}${at.search}`, target).href;
}

export interface LeaveEnvironment {
  serviceWorker?: Pick<ServiceWorkerContainer, 'getRegistrations'> | undefined;
  /** Отвечает ли новый адрес. По умолчанию — запрос к нему без cookie и CORS. */
  reachable?: (target: string) => Promise<boolean>;
  go?: (url: string) => void;
}

/**
 * Уйти на новый адрес: снять свой Service Worker и перейти.
 *
 * Service Worker снимается, иначе старый значок навсегда открывал бы оболочку
 * из кэша, не спрашивая сервер, — и до редиректа Caddy на новый адрес дело бы
 * не дошло. Локальная копия не трогается: к этому моменту очередь пуста, всё
 * лежит на сервере, а стирать то, что не мешает, незачем.
 *
 * Сначала проверяется, что новый адрес отвечает. Без сети переход не
 * состоится, а Service Worker уже снят, и старый значок вместо приложения
 * показал бы ошибку браузера. Возвращает `false`, если уйти не вышло.
 */
export async function leaveFor(target: string, env: LeaveEnvironment = {}): Promise<boolean> {
  const reachable = env.reachable ?? isReachable;
  if (!(await reachable(target))) return false;

  const container =
    'serviceWorker' in env ? env.serviceWorker : globalThis.navigator?.serviceWorker;
  try {
    const registrations = (await container?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Снять не вышло — уходим всё равно: новый адрес работает и так, а старый
    // значок покажет этот же экран ещё раз.
  }

  const go = env.go ?? ((url: string) => globalThis.location.replace(url));
  go(new URL('/', target).href);
  return true;
}

async function isReachable(target: string): Promise<boolean> {
  try {
    // `no-cors`: ответ чужого адреса прочитать нельзя, но и не нужно — важно,
    // что он пришёл. Без сети `fetch` бросает.
    await fetch(new URL('/healthz', target), { mode: 'no-cors', cache: 'no-store' });
    return true;
  } catch {
    return false;
  }
}

function currentOrigin(): string {
  return globalThis.location?.origin ?? '';
}
